import * as Yup from "yup";

const PasswordSchema = Yup.string().matches(
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{12,})/,
  "Doit contenir 12 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial"
);

export const LoginSchema = Yup.object({
  username: Yup.string()
    .min(3, "Identifiant invalide")
    .required("Identifiant requis"),
  password: Yup.string().required("Mot de passe requis"),
});

export const ForgotPasswordSchema = Yup.object({
  username: Yup.string()
    .min(3, "Identifiant invalide")
    .required("Identifiant requis"),
});

export const ChangePasswordSchema = Yup.object({
  password: PasswordSchema.required("Mot de passe requis"),
  passwordConfirmation: Yup.string().test(
    "passwords-match",
    "Les mots de passe ne correspondent pas",
    function (value) {
      return this.parent.password === value;
    }
  ),
});

export const getUserSchema = (isNew: boolean) => {
  return Yup.object({
    login: Yup.string()
      .min(3, "Identifiant très court")
      .max(100, "Identifiant très long")
      .required("Identifiant requis"),
    email: Yup.string()
      .email("Email invalide")
      .max(100, "Email très long")
      .required("Email requis"),
    prenom: Yup.string()
      .min(3, "Prénom très court")
      .max(20, "Prénom très long")
      .matches(
        /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆŠŽ∂ð ,.'-]+$/u,
        "Prénom invalide"
      )
      .required("Prénom requis"),
    nom: Yup.string()
      .min(3, "Nom très court")
      .max(20, "Nom très long")
      .matches(
        /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆŠŽ∂ð ,.'-]+$/u,
        "Nom de famille invalide"
      )
      .required("Nom de famille requis"),
    adresse: Yup.string().nullable(),
    complAdresse: Yup.string().nullable(),
    nomEntreprise: Yup.string().required("Entreprise requis"),
    ville: Yup.string().nullable(),
    password: isNew
      ? PasswordSchema.required("Mot de passe requis")
      : PasswordSchema,
    passwordConfirmation: Yup.string().test(
      "passwords-match",
      "Les mots de passe ne correspondent pas",
      function (value) {
        return this.parent.password === value;
      }
    ),
    indicActif: Yup.bool(),
    aPourRoles: Yup.array().of(
      Yup.object().shape({
        operationRefOperation: Yup.number(),
        roleRefRole: Yup.number(),
      })
    ),
  });
};

export const getInitialReserveValues = (lotId?: number) => {
  return {
    libelle: "",
    libelleMoe: "",
    indicSupprime: false,
    commentaire: "",
    urlDocLevee: undefined,
    coordonneeX: 0,
    coordonneeY: 0,
    planRefPlan: null,
    corpsEtatEntrepriseRefCorpsEtatEntreprise: null,
    lotRefLot: lotId || null,
    phaseRefPhase: 4,
    statutRefStatut: 1,
    aPourLocalisations: [],
    aPourSituations: [],
    dateEcheance: null,
    dateLevee: null,
    numeroDemandeSalesforce: null,
    copy: false,
  };
};

export const getReserveSchema = (isNew: boolean) => {
  return Yup.object({
    phaseRefPhase: Yup.number().required("Champs requis"),
    statutRefStatut: Yup.number().required("Champs requis"),
    lotRefLot: Yup.number().required("Champs requis"),
    libelle: Yup.string().nullable(),
    libelleMoe: Yup.string().nullable(),
    indicSupprime: Yup.bool(),
    commentaire: Yup.string().nullable(),
    urlDocLevee: Yup.string().nullable(),
    coordonneeX: Yup.number(),
    coordonneeY: Yup.number(),
    planRefPlan: Yup.number().nullable(),
    corpsEtatEntrepriseRefCorpsEtatEntreprise: Yup.number().nullable(),
    aPourLocalisations: Yup.array().of(Yup.number()).min(1, "Champs requis"),
    aPourSituations: Yup.array().of(Yup.number()),
    dateEcheance: Yup.string().nullable(),
    numeroDemandeSalesforce: Yup.string().nullable(),
    copy: Yup.boolean(),
  });
};

export const InitialLotValues = {
  refModeObtentionAdlr: null,
  dateObtentionLvadlr: null,
  dateEnvoiLvadlr: null,
  dateRefusAdlr: null,
  commentaire: "",
  locataire: {
    civilite: null,
    nom: "",
    prenom: "",
    telDom: "",
    telMob: "",
    email: "",
  },
  dateVisiteCloison: null,
  dateVisiteOpr: null,
  dateVisitePreLivraison: null,
  dateVisiteLivraison: null,
};

export const LotSchema = Yup.object({
  refModeObtentionAdlr: Yup.number().nullable(),
  dateObtentionLvadlr: Yup.string().nullable(),
  dateEnvoiLvadlr: Yup.string().nullable(),
  dateRefusAdlr: Yup.string().nullable(),
  commentaire: Yup.string().nullable(),
  locataire: Yup.object()
    .shape({
      civilite: Yup.string().nullable(),
      prenom: Yup.string().nullable(),
      nom: Yup.string().nullable(),
      telDom: Yup.string().nullable(),
      telMob: Yup.string().nullable(),
      email: Yup.string().nullable(),
    })
    .nullable(),
  dateVisiteCloison: Yup.string().nullable(),
  dateVisiteOpr: Yup.string().nullable(),
  dateVisitePreLivraison: Yup.string().nullable(),
  dateVisiteLivraison: Yup.string().nullable(),
});

export const InitialAppVersionValues = {
  file: undefined,
  description: "",
  disclaimer: "",
  mandatorySince: null,
  active: true,
};

export const AppVersionSchema = Yup.object({
  disclaimer: Yup.string()
    .min(4)
    .max(1024)
    .required("Message d'installation requis"),
  mandatorySince: Yup.string().nullable(),
  active: Yup.string().required("Date d'installation requis"),
});

export const InitialMessageValues = {
  message: "",
  dateFin: null,
};

export const MessageSchema = Yup.object({
  message: Yup.string().min(4).max(4000).required("Message requis"),
  dateFin: Yup.string().nullable(),
});
